/**
 * Client-side ZIP → state lookup. Mirrors functions/api/_zip_state.ts so the
 * engine can derive + display the user's state inline as soon as they type a
 * 5-digit ZIP, without round-tripping to the server. The Pages Function still
 * performs an authoritative check at submit-time — this module is for live UX
 * feedback only.
 *
 * Source: USPS Publication 28 + Wikipedia "ZIP Code prefixes". The table maps
 * 3-digit ZIP prefixes (USPS SCFs) to 2-letter state codes. Unrecognized
 * prefixes (military APO, minor territories) return the empty string.
 */

interface Zip3Range {
  min: string;
  max: string;
  state: string;
}

const RANGES: Zip3Range[] = [
  { min: "005", max: "005", state: "NY" },
  { min: "010", max: "027", state: "MA" },
  { min: "028", max: "029", state: "RI" },
  { min: "030", max: "038", state: "NH" },
  { min: "039", max: "049", state: "ME" },
  { min: "050", max: "059", state: "VT" },
  { min: "060", max: "069", state: "CT" },
  { min: "070", max: "089", state: "NJ" },
  { min: "100", max: "149", state: "NY" },
  { min: "150", max: "196", state: "PA" },
  { min: "197", max: "199", state: "DE" },
  { min: "200", max: "205", state: "DC" },
  { min: "206", max: "219", state: "MD" },
  { min: "220", max: "246", state: "VA" },
  { min: "247", max: "268", state: "WV" },
  { min: "270", max: "289", state: "NC" },
  { min: "290", max: "299", state: "SC" },
  { min: "300", max: "319", state: "GA" },
  { min: "320", max: "339", state: "FL" },
  { min: "341", max: "342", state: "FL" },
  { min: "344", max: "344", state: "FL" },
  { min: "346", max: "347", state: "FL" },
  { min: "349", max: "349", state: "FL" },
  { min: "350", max: "369", state: "AL" },
  { min: "370", max: "385", state: "TN" },
  { min: "386", max: "397", state: "MS" },
  { min: "398", max: "399", state: "GA" },
  { min: "400", max: "427", state: "KY" },
  { min: "430", max: "459", state: "OH" },
  { min: "460", max: "479", state: "IN" },
  { min: "480", max: "499", state: "MI" },
  { min: "500", max: "528", state: "IA" },
  { min: "530", max: "549", state: "WI" },
  { min: "550", max: "567", state: "MN" },
  { min: "570", max: "577", state: "SD" },
  { min: "580", max: "588", state: "ND" },
  { min: "590", max: "599", state: "MT" },
  { min: "600", max: "629", state: "IL" },
  { min: "630", max: "658", state: "MO" },
  { min: "660", max: "679", state: "KS" },
  { min: "680", max: "693", state: "NE" },
  { min: "700", max: "714", state: "LA" },
  { min: "716", max: "729", state: "AR" },
  { min: "730", max: "749", state: "OK" },
  { min: "750", max: "799", state: "TX" },
  { min: "800", max: "816", state: "CO" },
  { min: "820", max: "831", state: "WY" },
  { min: "832", max: "838", state: "ID" },
  { min: "840", max: "847", state: "UT" },
  { min: "850", max: "865", state: "AZ" },
  { min: "870", max: "884", state: "NM" },
  { min: "889", max: "898", state: "NV" },
  { min: "900", max: "961", state: "CA" },
  { min: "967", max: "968", state: "HI" },
  { min: "970", max: "979", state: "OR" },
  { min: "980", max: "994", state: "WA" },
  { min: "995", max: "999", state: "AK" },
];

const STATE_CODE_TO_NAME: Record<string, string> = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California",
  CO: "Colorado", CT: "Connecticut", DE: "Delaware", DC: "District of Columbia",
  FL: "Florida", GA: "Georgia", HI: "Hawaii", ID: "Idaho", IL: "Illinois",
  IN: "Indiana", IA: "Iowa", KS: "Kansas", KY: "Kentucky", LA: "Louisiana",
  ME: "Maine", MD: "Maryland", MA: "Massachusetts", MI: "Michigan", MN: "Minnesota",
  MS: "Mississippi", MO: "Missouri", MT: "Montana", NE: "Nebraska", NV: "Nevada",
  NH: "New Hampshire", NJ: "New Jersey", NM: "New Mexico", NY: "New York",
  NC: "North Carolina", ND: "North Dakota", OH: "Ohio", OK: "Oklahoma",
  OR: "Oregon", PA: "Pennsylvania", RI: "Rhode Island", SC: "South Carolina",
  SD: "South Dakota", TN: "Tennessee", TX: "Texas", UT: "Utah", VT: "Vermont",
  VA: "Virginia", WA: "Washington", WV: "West Virginia", WI: "Wisconsin",
  WY: "Wyoming",
};

/**
 * Resolve a 5-digit US ZIP to its state code. Returns "" when the input isn't
 * a 5-digit ZIP or the prefix isn't covered by the USPS table.
 */
export function zipToState(zip: string): string {
  const cleaned = String(zip || "").trim().split("-")[0];
  if (!/^\d{5}$/.test(cleaned ?? "")) return "";
  const prefix = cleaned!.slice(0, 3);
  for (const r of RANGES) {
    if (prefix >= r.min && prefix <= r.max) return r.state;
  }
  return "";
}

/** Full state name for a 2-letter code, or "" if unrecognized. */
export function stateName(code: string): string {
  return STATE_CODE_TO_NAME[code] ?? "";
}
